import React from 'react';

export interface EventFormData {
  title: string;
  description: string;
  venue: string;
  date_time: string;
  end_time: string;
  max_attendees: number;
  reserved_tickets: number;
  admission_policy: 'review' | 'auto';
  waitlist_when_full: boolean;
  show_attendee_list: boolean;
  event_type: 'social' | 'meetup' | 'conference' | 'casual';
  google_maps_link: string;
  entry_requirement?: string;
  badge_subtitle?: string;
  badge_title?: string;
}

interface EventEditFormProps {
  formData: EventFormData;
  loading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onChange: (data: EventFormData) => void;
  onCancel: () => void;
}

const EventEditForm: React.FC<EventEditFormProps> = ({
  formData,
  loading,
  onSubmit,
  onChange,
  onCancel,
}) => {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title
        </label>
        <input
          type="text"
          required
          value={formData.title}
          onChange={(e) => onChange({ ...formData, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => onChange({ ...formData, description: e.target.value })}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Start Date & Time
        </label>
        <input
          type="datetime-local"
          required
          value={formData.date_time}
          onChange={(e) => onChange({ ...formData, date_time: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          End Date & Time
        </label>
        <input
          type="datetime-local"
          value={formData.end_time}
          onChange={(e) => onChange({ ...formData, end_time: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="mt-1 text-sm text-gray-500">
          Optional - defaults to 2 hours after start time
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Venue
        </label>
        <input
          type="text"
          required
          value={formData.venue}
          onChange={(e) => onChange({ ...formData, venue: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Google Maps Link
        </label>
        <input
          type="url"
          value={formData.google_maps_link || ''}
          onChange={(e) => onChange({ ...formData, google_maps_link: e.target.value })}
          placeholder="https://maps.google.com/..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="mt-1 text-sm text-gray-500">
          Optional - paste a Google Maps link for the venue location
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Badge footer (optional)
        </label>
        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            maxLength={100}
            value={formData.badge_subtitle || ''}
            onChange={(e) => onChange({ ...formData, badge_subtitle: e.target.value })}
            placeholder="Left, e.g. 🚀 Star IX"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            maxLength={100}
            value={formData.badge_title || ''}
            onChange={(e) => onChange({ ...formData, badge_title: e.target.value })}
            placeholder="Right, defaults to the event title"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Printed in the blue band at the bottom of name badges. Emoji are fine. Leave the left blank to centre the right-hand text.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Condition of entry (optional)
        </label>
        <textarea
          value={formData.entry_requirement || ''}
          onChange={(e) => onChange({ ...formData, entry_requirement: e.target.value })}
          rows={3}
          placeholder="e.g. Valid, in-date physical photo ID is required at reception. Copies and photos of ID will not be accepted."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="mt-1 text-sm text-gray-500">
          Shown highlighted on the sign-up form. Attendees must tick a box to confirm they will comply.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Event Type
        </label>
        <select
          required
          value={formData.event_type}
          onChange={(e) => onChange({ ...formData, event_type: e.target.value as 'social' | 'meetup' | 'conference' | 'casual' })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="social">Social</option>
          <option value="meetup">Meetup</option>
          <option value="conference">Conference</option>
          <option value="casual">Casual</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Max Attendees
        </label>
        <input
          type="number"
          required
          min="1"
          value={formData.max_attendees}
          onChange={(e) => onChange({ ...formData, max_attendees: parseInt(e.target.value) })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Reserved Tickets
        </label>
        <input
          type="number"
          min="0"
          value={formData.reserved_tickets}
          onChange={(e) => onChange({ ...formData, reserved_tickets: parseInt(e.target.value) || 0 })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="mt-1 text-sm text-gray-500">
          Held back from public self-registration; you can still allocate them by approving people
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Admission
        </label>
        <select
          value={formData.admission_policy}
          onChange={(e) => onChange({ ...formData, admission_policy: e.target.value as 'review' | 'auto' })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="review">Curated — you approve every request</option>
          <option value="auto">Self-service — instant tickets while spots remain</option>
        </select>
        <p className="mt-1 text-sm text-gray-500">
          {formData.admission_policy === 'review'
            ? 'The public page offers "Request an invitation" and never says sold out. Invited guests still skip the queue.'
            : 'Tickets are issued automatically until self-service spots run out.'}
        </p>
      </div>

      {formData.admission_policy === 'auto' && (
        <div className="flex items-center">
          <input
            type="checkbox"
            id="waitlist_when_full"
            checked={formData.waitlist_when_full}
            onChange={(e) => onChange({ ...formData, waitlist_when_full: e.target.checked })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="waitlist_when_full" className="ml-2 block text-sm text-gray-700">
            Offer a waitlist when full (instead of closing registration)
          </label>
        </div>
      )}

      <div className="flex items-center">
        <input
          type="checkbox"
          id="show_attendee_list"
          checked={formData.show_attendee_list}
          onChange={(e) => onChange({ ...formData, show_attendee_list: e.target.checked })}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="show_attendee_list" className="ml-2 block text-sm text-gray-700">
          Show attendee list publicly
        </label>
      </div>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default EventEditForm;